/** Minimal accessible modal shell for the OMS operator action dialogs. Tailwind
 *  only — no new UI dependency. Closes on backdrop click + Escape. */

import { useEffect, type ReactNode } from 'react'

export function OmsDialog({
  title,
  onClose,
  children,
  footer,
}: {
  title: string
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Mark the press handled so outer Escape handlers (the dashboard's
      // full-screen map) do not ALSO close a layer on the same keystroke.
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className="w-full max-w-md bg-white rounded-lg shadow-xl border border-gray-200 flex flex-col max-h-[90vh]"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
          <button
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-700 text-lg leading-none"
          >
            &times;
          </button>
        </div>
        <div className="px-4 py-4 overflow-y-auto space-y-3 text-sm">{children}</div>
        {footer && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>
  )
}
